import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useMemoizedFn from '@/utils/useMemoizedFn';

import { Box } from '@/components/Box';
import { Loader } from '@/components/Loader';
import { Text } from '@/components/Text';
import { Typography } from '@/components/Typography';
import {
  calcMinDepositPercentage,
  calculate,
  getFinanceProduct,
  getFinanceProducts,
} from '@/lib/payment/v12';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { DepositSlider } from './DepositSlider';
import { DurationSlider } from './DurationSlider';
import {
  ErrorMessage,
  InstallmentSummaryWrapper,
  StyledTable,
  Wrapper,
} from './LoanCalculator.style';
import type { V12Payments, V12Product } from './type';

import * as M from './messages';

interface V12LoanCalculatorProps {
  amount: number;
  currency: string;
  disabled?: boolean;
  onChange?: (deposit: string, duration: number) => void;
  setIsValid?: (b: boolean) => void;
}

export const V12LoanCalculator = ({
  amount,
  currency,
  disabled,
  onChange,
  setIsValid,
}: V12LoanCalculatorProps) => {
  const { formatPrice } = useRegionProvider();
  const { v12PaymentMax, v12PaymentMin } = useConfigProvider();
  const [loanProducts, setLoanProducts] = useState<V12Product[] | null>(null);
  const [currentProduct, setCurrentProduct] = useState<V12Product | undefined>(
    undefined,
  );
  const [depositFactor, setDepositFactor] = useState<number>(0);
  const [payments, setPayments] = useState<V12Payments | null>(null);

  function getProducts() {
    const products: V12Product[] = getFinanceProducts();
    setLoanProducts(products);
    setCurrentProduct(products[0]);
    setDepositFactor(
      calcMinDepositPercentage(amount, products[0].maxLoan) ?? 0,
    );
  }
  useEffect(() => {
    init();
  }, []);

  const init = useMemoizedFn(() => {
    getProducts();
  });

  useEffect(() => {
    if (currentProduct) {
      const cashPrice = amount;

      let deposit = parseFloat(
        (Math.ceil(cashPrice * depositFactor) / 100).toFixed(2),
      );

      if (deposit > currentProduct.maxLoan) {
        deposit = currentProduct.maxLoan;
      }

      const financeProduct = getFinanceProduct(currentProduct?.productId);
      const calcPayments = calculate(financeProduct, cashPrice, deposit);

      setPayments(calcPayments);
      if (setIsValid) {
        setIsValid(
          v12PaymentMax !== undefined &&
            v12PaymentMin !== undefined &&
            amount <= v12PaymentMax &&
            amount >= v12PaymentMin,
        );
      }
      if (onChange) {
        onChange(calcPayments.deposit, calcPayments.months);
      }
    }
  }, [
    currentProduct,
    depositFactor,
    amount,
    v12PaymentMax,
    v12PaymentMin,
    setIsValid,
    onChange,
  ]);

  if (v12PaymentMin === undefined || v12PaymentMax === undefined) {
    return <Loader height={400} />;
  } else if (amount < v12PaymentMin || amount > v12PaymentMax) {
    return (
      <ErrorMessage data-test-id='ErrorsTypography' scale='p3'>
        <M.ErrorV12AmountLimit
          maxAmount={`${currency} ${v12PaymentMax}`}
          minAmount={`${currency} ${v12PaymentMin}`}
        />
      </ErrorMessage>
    );
  }

  return (
    <>
      {loanProducts && currentProduct && payments && (
        <Wrapper $disabled={disabled}>
          <Text>
            {M.Description(
              formatPrice(
                { amount: currentProduct.minLoan, currency },
                { maximumFractionDigits: 0 },
              ),
            )}
          </Text>
          <Box py={4}>
            <Typography
              fontWeight='bold'
              mb={1}
              scale='h5'
              textTransform='uppercase'
            >
              <FormattedMessage {...M.defined.adjustYourDeposit} />
            </Typography>
            <Typography>
              <FormattedMessage {...M.defined.useSliderToAdjustYourDeposit} />
            </Typography>
            <Typography fontWeight='bold' mb={2}>
              <FormattedMessage {...M.defined.currentSelection} />{' '}
              {formatPrice(
                { amount: Number(payments.deposit), currency },
                { maximumFractionDigits: 2 },
              )}
            </Typography>

            <DepositSlider
              amount={amount}
              maxLoan={currentProduct.maxLoan}
              minLoan={currentProduct.minLoan}
              onChange={setDepositFactor}
              value={depositFactor}
            />

            <Typography
              fontWeight='bold'
              mb={1}
              scale='h5'
              textTransform='uppercase'
            >
              <FormattedMessage {...M.defined.selectATermDuration} />
            </Typography>
            <Typography fontWeight='bold' mb={2}>
              <FormattedMessage {...M.defined.currentSelection} />{' '}
              {currentProduct.months} <FormattedMessage {...M.defined.months} />
            </Typography>
            <DurationSlider
              loanProducts={loanProducts}
              onChange={setCurrentProduct}
              value={currentProduct}
            />
            <Text>
              {currentProduct.apr}%{' '}
              <FormattedMessage {...M.defined.aprRepresentative} />
            </Text>
          </Box>

          <InstallmentSummaryWrapper>
            <StyledTable>
              <tbody>
                <tr>
                  <td>
                    <FormattedMessage {...M.defined.deposit} />
                  </td>
                  <td>
                    {formatPrice(
                      { amount: Number(payments.deposit), currency },
                      { maximumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...M.defined.totalAmountOfCredit} />
                  </td>
                  <td>
                    {formatPrice(
                      { amount: Number(payments.loanAmount), currency },
                      { maximumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
                <tr className='bold'>
                  <td>
                    <FormattedMessage {...M.defined.monthlyPayment} />
                  </td>
                  <td>
                    {formatPrice(
                      { amount: Number(payments.initialPayments), currency },
                      { maximumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
                <tr className='bold'>
                  <td>
                    <FormattedMessage {...M.defined.loanTerm} />
                  </td>
                  <td>
                    {payments.months} <FormattedMessage {...M.defined.months} />
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...M.defined.representativeAPR} />
                  </td>
                  <td>
                    {payments.apr}%{/* </Typography> */}
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage
                      {...M.defined.fixedAnnualRateOfInterest}
                    />
                  </td>
                  <td>{payments.annualRate}%</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage {...M.defined.totalPrice} />
                  </td>
                  <td>
                    {formatPrice(
                      { amount: Number(payments.cashPrice), currency },
                      { maximumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
                <tr className='bold'>
                  <td>
                    <FormattedMessage {...M.defined.totalAmountPayable} />
                  </td>
                  <td>
                    {formatPrice(
                      { amount: Number(payments.amountPayable), currency },
                      { maximumFractionDigits: 2 },
                    )}
                  </td>
                </tr>
              </tbody>
            </StyledTable>
          </InstallmentSummaryWrapper>
        </Wrapper>
      )}
    </>
  );
};
